import { Button, tokens } from '@fluentui/react-components';
import { useRef } from 'react';

import { renderVarHighlight } from '../VarHighlight/VarHighlight.js';

import type { BodyPanelProps } from './BodyPanel.interface.js';

// These must match exactly between backdrop and textarea
const FONT_FAMILY = "'Cascadia Code', 'Consolas', monospace";
const FONT_SIZE = '12px';
const LINE_HEIGHT = '1.5';
const PADDING = '8px';

export function BodyPanel({ body, vars, onChange }: BodyPanelProps) {
  const backdropRef = useRef<HTMLDivElement>(null);

  function prettify() {
    try {
      const parsed: unknown = JSON.parse(body);
      onChange(JSON.stringify(parsed, null, 2));
    } catch {
      // not valid JSON
    }
  }

  function handleScroll(e: React.UIEvent<HTMLTextAreaElement>) {
    if (backdropRef.current) {
      backdropRef.current.scrollTop = e.currentTarget.scrollTop;
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: PADDING,
        gap: '4px',
        boxSizing: 'border-box',
      }}
    >
      <div>
        <Button appearance="subtle" size="small" onClick={prettify}>
          Prettify JSON
        </Button>
      </div>

      <div style={{ position: 'relative', flex: 1 }}>
        {/* Backdrop: renders colored spans behind the transparent textarea */}
        <div
          ref={backdropRef}
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            fontFamily: FONT_FAMILY,
            fontSize: FONT_SIZE,
            lineHeight: LINE_HEIGHT,
            padding: PADDING,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            overflow: 'hidden',
            pointerEvents: 'none',
            backgroundColor: tokens.colorNeutralBackground3,
            color: tokens.colorNeutralForeground1,
            border: '1px solid transparent',
            borderRadius: '4px',
            boxSizing: 'border-box',
          }}
        >
          {body ? (
            renderVarHighlight(body, vars)
          ) : (
            <span style={{ color: tokens.colorNeutralForeground4 }}>
              Request body (JSON)
            </span>
          )}
          {/* Extra newline keeps backdrop height in sync when body ends with \n */}
          {'\n'}
        </div>

        {/* Actual textarea: transparent text so backdrop shows through */}
        <textarea
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            fontFamily: FONT_FAMILY,
            fontSize: FONT_SIZE,
            lineHeight: LINE_HEIGHT,
            padding: PADDING,
            whiteSpace: 'pre-wrap',
            resize: 'none',
            backgroundColor: 'transparent',
            color: 'transparent',
            caretColor: tokens.colorNeutralForeground1,
            border: `1px solid ${tokens.colorNeutralStroke1}`,
            borderRadius: '4px',
            outline: 'none',
            zIndex: 1,
            overflow: 'auto',
            boxSizing: 'border-box',
          }}
          value={body}
          onChange={(e) => onChange(e.target.value)}
          onScroll={handleScroll}
          spellCheck={false}
        />
      </div>
    </div>
  );
}
